import { Outlet, createRootRouteWithContext, useLocation, useNavigate } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useAuth } from "../auth/auth";
import { Choose } from "../components/choose";
import { Shell } from "../components/shell";
import { SignIn } from "../components/sign-in";
import { useMe } from "../api/hooks";
import { ErrorBox, Loading } from "../components/ui";
import { isPlatformAdminToken } from "../lib/portal";
import { usePortal } from "../lib/portal-context";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: Root });

function Root() {
  const auth = useAuth();
  const location = useLocation();
  if (location.pathname.startsWith("/auth/")) return <Outlet />;
  if (!auth.tokens) return <SignIn />;
  return <Gate />;
}

function Gate() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Decided from the token, synchronously, before the first paint. For everyone who is
  // not a platform admin this is false on the very first render and the picker code
  // below is never reached — there is no request to wait for and nothing to flicker.
  const platform = useMemo(() => isPlatformAdminToken(auth.tokens?.id_token), [auth.tokens]);
  const { portal, setPortal } = usePortal();
  // Regular users are always in their organisation; the picker code below never runs
  // for them. Platform admins are asked on every fresh landing at the front door.
  const choice = platform ? portal : "org";
  const atFrontDoor = platform && location.pathname === "/";
  // "Humindly admin" at the front door means the admin portal. The redirect runs as an
  // effect, and until it lands we draw the same spinner as any other load — never the
  // outlet, so the dashboard is not painted underneath.
  useEffect(() => {
    if (atFrontDoor && choice === "admin") void navigate({ to: "/admin", replace: true });
  }, [atFrontDoor, choice, navigate]);
  const me = useMe();

  if (atFrontDoor && choice === null) return <Choose onChoose={setPortal} />;
  // A failed request that is already being retried is still a load, not an error: no
  // flash of "something went wrong" between a hiccup and its retry.
  if (me.isPending || (atFrontDoor && choice === "admin") || (me.isError && me.isFetching)) {
    return <div className="min-h-screen grid place-items-center"><Loading /></div>;
  }
  if (me.isError) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="w-[420px]"><ErrorBox error={me.error} /></div>
      </div>
    );
  }
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}
